/**
 * Film Data Context
 */

import { createContext, useContext, useState, useCallback } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';

const FilmContext = createContext(null);

export function FilmProvider({ children }) {
    const [films, setFilms] = useState([]);
    const [schedule, setSchedule] = useState(null);
    const [genres, setGenres] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const apiBase = window.rialtoData?.apiUrl || '/wp-json/rialto/v1/';

    // Fetch films with filters
    // By default, uses deduplication (unique=1) to show one entry per TV series
    const fetchFilms = useCallback(async (params = {}, options = { unique: true }) => {
        setLoading(true);
        setError(null);

        try {
            // Add unique parameter for deduplication unless explicitly disabled
            const queryParams = { ...params };
            if (options.unique !== false) {
                queryParams.unique = 1;
            }

            const queryString = new URLSearchParams(queryParams).toString();
            const response = await apiFetch({
                path: `rialto/v1/films${queryString ? '?' + queryString : ''}`,
            });

            setFilms(response.films || []);
            return response;
        } catch (err) {
            setError(err.message);
            return { films: [], total: 0 };
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch single film
    const fetchFilm = useCallback(async (filmId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiFetch({
                path: `rialto/v1/films/${filmId}`,
            });
            return response;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch schedule for a date
    const fetchSchedule = useCallback(async (date) => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiFetch({
                path: `rialto/v1/schedule?date=${date}`,
            });

            setSchedule(response);
            return response;
        } catch (err) {
            setError(err.message);
            return { schedule: [] };
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch week schedule
    const fetchWeekSchedule = useCallback(async (startDate) => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiFetch({
                path: `rialto/v1/schedule/week?start_date=${startDate}`,
            });
            return response;
        } catch (err) {
            setError(err.message);
            return { days: [] };
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch what's playing now
    const fetchWhatsPlaying = useCallback(async () => {
        try {
            const response = await apiFetch({
                path: 'rialto/v1/whats-playing',
            });
            return response;
        } catch (err) {
            setError(err.message);
            return { playing: [] };
        }
    }, []);

    // Fetch pick of the day
    const fetchPickOfDay = useCallback(async (date) => {
        try {
            const response = await apiFetch({
                path: `rialto/v1/pick-of-day${date ? '?date=' + date : ''}`,
            });
            return response;
        } catch (err) {
            setError(err.message);
            return { pick: null };
        }
    }, []);

    // Fetch pick of the week
    const fetchPickOfWeek = useCallback(async () => {
        try {
            const response = await apiFetch({
                path: 'rialto/v1/pick-of-week',
            });
            return response;
        } catch (err) {
            setError(err.message);
            return { pick: null };
        }
    }, []);

    // Fetch genres
    const fetchGenres = useCallback(async () => {
        try {
            const response = await apiFetch({
                path: 'rialto/v1/genres',
            });
            setGenres(response.genres || []);
            return response.genres;
        } catch (err) {
            return [];
        }
    }, []);

    // Search films
    const searchFilms = useCallback(async (params = {}) => {
        setLoading(true);
        setError(null);

        try {
            const queryString = new URLSearchParams(params).toString();
            const response = await apiFetch({
                path: `rialto/v1/search?${queryString}`,
            });

            setFilms(response.films || []);
            return response;
        } catch (err) {
            setError(err.message);
            return { films: [], total: 0 };
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch related films
    const fetchRelatedFilms = useCallback(async (filmId, limit = 6) => {
        try {
            const response = await apiFetch({
                path: `rialto/v1/related/${filmId}?limit=${limit}`,
            });
            return response.related || [];
        } catch (err) {
            return [];
        }
    }, []);

    const value = {
        films,
        schedule,
        genres,
        loading,
        error,
        fetchFilms,
        fetchFilm,
        fetchSchedule,
        fetchWeekSchedule,
        fetchWhatsPlaying,
        fetchPickOfDay,
        fetchPickOfWeek,
        fetchGenres,
        searchFilms,
        fetchRelatedFilms,
    };

    return (
        <FilmContext.Provider value={value}>
            {children}
        </FilmContext.Provider>
    );
}

export function useFilms() {
    const context = useContext(FilmContext);
    if (!context) {
        throw new Error('useFilms must be used within FilmProvider');
    }
    return context;
}
